<template>
  <div class="training-protocol-hub">
    <input
      ref="fileInput"
      class="hidden-file-input"
      type="file"
      accept="image/jpeg,image/png,image/webp,image/avif,image/gif,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.webp,.avif,.gif,.heic,.heif,.pdf"
      multiple
      @change="handleFileInput" />

    <v-dialog v-model="archiveDialog" max-width="1220" dark scrollable :fullscreen="$vuetify.breakpoint.smAndDown">
      <v-card class="archive-card">
        <header class="dialog-header archive-header">
          <span class="dialog-header-icon"><v-icon>mdi-archive-eye</v-icon></span>
          <div class="dialog-header-copy">
            <div>Schulungsdokumentation</div>
            <h2>Fotoprotokoll-Archiv</h2>
            <p>Vergangene Schulungen, Entwürfe und fertige Protokolle an einem Ort.</p>
          </div>
          <v-btn icon class="close-action" aria-label="Archiv schliessen" @click="archiveDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <v-card-text class="archive-body">
          <div class="archive-stats">
            <article>
              <span class="stat-icon completed"><v-icon>mdi-check-decagram-outline</v-icon></span>
              <div><strong>{{ archiveCounts.completed }}</strong><small>Fertig</small></div>
            </article>
            <article>
              <span class="stat-icon draft"><v-icon>mdi-file-edit-outline</v-icon></span>
              <div><strong>{{ archiveCounts.draft }}</strong><small>Entwürfe</small></div>
            </article>
            <article>
              <span class="stat-icon missing"><v-icon>mdi-camera-plus-outline</v-icon></span>
              <div><strong>{{ archiveCounts.missing }}</strong><small>Noch offen</small></div>
            </article>
          </div>

          <section class="archive-filter-panel">
            <v-text-field
              v-model="archiveSearch"
              outlined
              dense
              dark
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              label="Schulung, Ort oder Protokoll suchen"></v-text-field>
            <v-select
              v-model="archiveYear"
              :items="archiveYearOptions"
              outlined
              dense
              dark
              hide-details
              label="Jahr"></v-select>
            <v-btn-toggle v-model="archiveStatus" mandatory dense rounded dark class="archive-status-filter">
              <v-btn value="all">Alle</v-btn>
              <v-btn value="completed">Fertig</v-btn>
              <v-btn value="draft">Entwurf</v-btn>
              <v-btn value="missing">Offen</v-btn>
            </v-btn-toggle>
          </section>

          <div v-if="loadingProtocols" class="archive-loading">
            <v-progress-circular indeterminate color="#58d388" size="32" width="3"></v-progress-circular>
            <span>Protokolle werden geladen ...</span>
          </div>

          <div v-else-if="filteredArchiveItems.length" class="archive-grid">
            <article
              v-for="item in filteredArchiveItems"
              :key="item.session._id"
              class="archive-item"
              role="button"
              tabindex="0"
              @click="openEditor(item.session)"
              @keydown.enter.prevent="openEditor(item.session)"
              @keydown.space.prevent="openEditor(item.session)">
              <div class="archive-cover" :class="{ 'has-file': Boolean(coverFile(item.protocol)) }">
                <secured-media
                  v-if="coverFile(item.protocol) && canPreviewImage(coverFile(item.protocol))"
                  :src="protocolPreviewPath(item.session._id, coverFile(item.protocol).fileId, 'thumbnail')"
                  :cache-key="`training-protocol-thumbnail-${coverFile(item.protocol).fileId}`"
                  media-type="image"
                  class="archive-cover-media"></secured-media>
                <span v-else class="archive-cover-placeholder">
                  <v-icon>{{ item.protocol ? "mdi-image-multiple-outline" : "mdi-camera-plus-outline" }}</v-icon>
                </span>
                <span class="archive-status-pill" :class="`is-${item.status}`">{{ statusLabel(item.status) }}</span>
                <span v-if="item.protocol" class="archive-file-count">
                  <v-icon>mdi-paperclip</v-icon>{{ item.protocol.fileCount || 0 }}
                </span>
              </div>
              <div class="archive-item-copy">
                <small>{{ formatLongDate(item.session.startsAt) }}</small>
                <h3>{{ item.session.title }}</h3>
                <p><v-icon>mdi-map-marker-outline</v-icon>{{ item.session.location || "Ort nicht hinterlegt" }}</p>
                <div class="archive-item-footer">
                  <span v-if="item.protocol">{{ updatedText(item.protocol) }}</span>
                  <span v-else>Fotoprotokoll noch erstellen</span>
                  <v-icon>mdi-chevron-right</v-icon>
                </div>
              </div>
            </article>
          </div>

          <div v-else class="archive-empty">
            <span><v-icon>mdi-file-search-outline</v-icon></span>
            <h3>Keine Schulungen gefunden</h3>
            <p>Passe Suche, Jahr oder Statusfilter an.</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editorDialog" persistent dark scrollable max-width="1120" :fullscreen="$vuetify.breakpoint.smAndDown">
      <v-card class="editor-card">
        <header class="dialog-header editor-header">
          <span class="dialog-header-icon"><v-icon>mdi-camera-plus-outline</v-icon></span>
          <div class="dialog-header-copy">
            <div>Fotoprotokoll</div>
            <h2>{{ activeSession ? activeSession.title : "Schulung" }}</h2>
            <p>{{ activeSessionMeta }}</p>
          </div>
          <span v-if="protocol" class="editor-status-pill" :class="`is-${protocol.status}`">{{ statusLabel(protocol.status) }}</span>
          <v-btn icon class="close-action" :disabled="saving || uploading" aria-label="Fotoprotokoll schliessen" @click="closeEditor">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <v-card-text class="editor-body">
          <div v-if="editorLoading" class="editor-loading">
            <v-progress-circular indeterminate color="#58d388" size="36" width="3"></v-progress-circular>
            <span>Fotoprotokoll wird geöffnet ...</span>
          </div>

          <template v-else>
            <section class="session-summary">
              <span><v-icon>mdi-calendar-check-outline</v-icon></span>
              <div>
                <small>Dokumentiert wird</small>
                <strong>{{ activeSession ? activeSession.title : "Schulung" }}</strong>
                <p>{{ activeSessionMeta }}</p>
              </div>
            </section>

            <section class="editor-section details-section">
              <div class="section-heading">
                <span><v-icon>mdi-text-box-edit-outline</v-icon></span>
                <div>
                  <h3>Titel &amp; Zusammenfassung</h3>
                  <p>Halte die wichtigsten Erkenntnisse oder Besonderheiten der Schulung fest.</p>
                </div>
              </div>
              <v-text-field
                v-model="form.title"
                outlined
                dark
                counter="200"
                maxlength="200"
                label="Titel des Fotoprotokolls"
                :disabled="saving"></v-text-field>
              <v-textarea
                v-model="form.summary"
                outlined
                dark
                auto-grow
                rows="4"
                counter="8000"
                maxlength="8000"
                label="Zusammenfassung oder Notizen"
                hint="Optional: Ablauf, Erkenntnisse, offene Punkte oder Besonderheiten"
                persistent-hint
                :disabled="saving"></v-textarea>
            </section>

            <section class="editor-section media-section">
              <div class="section-heading media-heading">
                <span><v-icon>mdi-image-multiple-outline</v-icon></span>
                <div>
                  <h3>Fotos &amp; Dokumente</h3>
                  <p>Bis zu 50 Bilder oder PDF-Dateien, maximal 50 MB pro Datei.</p>
                </div>
                <span class="media-count">{{ files.length }} / 50</span>
              </div>

              <button
                type="button"
                class="upload-dropzone"
                :disabled="saving || uploading || files.length >= 50"
                @click="openFilePicker"
                @dragover.prevent
                @drop.prevent="handleFileDrop">
                <span class="upload-dropzone-icon"><v-icon>mdi-cloud-upload-outline</v-icon></span>
                <span class="upload-dropzone-copy">
                  <strong>{{ uploading ? uploadLabel : "Fotos oder PDF hinzufügen" }}</strong>
                  <small>Dateien auswählen oder hierhin ziehen</small>
                </span>
                <v-icon class="upload-dropzone-arrow">mdi-plus-circle-outline</v-icon>
              </button>

              <div v-if="uploading" class="upload-progress-panel">
                <div><span>{{ uploadLabel }}</span><strong>{{ Math.round(uploadProgress) }} %</strong></div>
                <v-progress-linear :value="uploadProgress" color="#58d388" height="8" rounded background-color="rgba(255,255,255,0.08)"></v-progress-linear>
              </div>

              <div v-if="files.length" class="protocol-files-grid">
                <article
                  v-for="(file, index) in files"
                  :key="file.key"
                  class="protocol-file-card"
                  :class="{ 'is-cover': file.isCover, 'is-dragging': dragIndex === index }"
                  draggable="true"
                  @dragstart="startFileDrag(index)"
                  @dragend="dragIndex = null"
                  @dragover.prevent
                  @drop.prevent="dropFileAt(index)">
                  <div
                    class="file-preview"
                    role="button"
                    tabindex="0"
                    @click="openFile(file, index)"
                    @keydown.enter.prevent="openFile(file, index)"
                    @keydown.space.prevent="openFile(file, index)">
                    <img v-if="file.previewUrl" :src="file.previewUrl" :alt="file.originalName" />
                    <secured-media
                      v-else-if="file.fileId && canPreviewImage(file)"
                      :src="protocolPreviewPath(activeSession._id, file.fileId, 'thumbnail')"
                      :cache-key="`training-protocol-thumbnail-${file.fileId}`"
                      media-type="image"></secured-media>
                    <span v-else class="file-placeholder" :class="{ pdf: isPdf(file) }">
                      <v-icon>{{ isPdf(file) ? "mdi-file-pdf-box" : "mdi-image-outline" }}</v-icon>
                      <small>{{ fileExtension(file.originalName) }}</small>
                    </span>
                    <span v-if="file.isCover" class="cover-label"><v-icon>mdi-star</v-icon>Titelbild</span>
                    <span v-if="!file.fileId" class="new-file-label">Neu</span>
                  </div>

                  <div class="file-copy">
                    <strong :title="file.originalName">{{ file.originalName }}</strong>
                    <small>{{ formatBytes(file.size) }}</small>
                  </div>

                  <v-text-field
                    v-model="file.caption"
                    outlined
                    dense
                    dark
                    hide-details
                    maxlength="500"
                    label="Bildunterschrift"
                    :disabled="saving"></v-text-field>

                  <div class="file-actions">
                    <v-btn
                      icon
                      small
                      :class="{ active: file.isCover }"
                      :disabled="!isImage(file) || saving"
                      :aria-label="file.isCover ? 'Titelbild ausgewählt' : 'Als Titelbild festlegen'"
                      @click="setCover(index)">
                      <v-icon>{{ file.isCover ? "mdi-star" : "mdi-star-outline" }}</v-icon>
                    </v-btn>
                    <v-btn icon small :disabled="index === 0 || saving" aria-label="Datei nach vorne verschieben" @click="moveFile(index, -1)">
                      <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      :disabled="index === files.length - 1 || saving"
                      aria-label="Datei nach hinten verschieben"
                      @click="moveFile(index, 1)">
                      <v-icon>mdi-arrow-right</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                      v-if="file.fileId"
                      icon
                      small
                      :disabled="saving"
                      aria-label="Originaldatei herunterladen"
                      @click="downloadFile(file)">
                      <v-icon>mdi-download-outline</v-icon>
                    </v-btn>
                    <v-btn icon small class="remove-file-action" :disabled="saving" aria-label="Datei entfernen" @click="removeFile(index)">
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </article>
              </div>

              <div v-else class="files-empty">
                <span><v-icon>mdi-image-plus-outline</v-icon></span>
                <h4>Noch keine Dateien hinterlegt</h4>
                <p>Füge Fotos der Schulung oder ein bereits erstelltes PDF-Protokoll hinzu.</p>
              </div>
            </section>

            <section v-if="protocol" class="protocol-meta">
              <v-icon>mdi-history</v-icon>
              <span>{{ updatedText(protocol) }}</span>
              <span v-if="protocol.completedAt">Abgeschlossen am {{ formatDateTime(protocol.completedAt) }}</span>
            </section>
          </template>
        </v-card-text>

        <v-card-actions class="editor-actions">
          <div class="editor-actions-copy">
            <strong>{{ files.length }} {{ files.length === 1 ? "Datei" : "Dateien" }}</strong>
            <small>Entwürfe bleiben nur für die Akademie-Administration sichtbar.</small>
          </div>
          <v-btn text class="secondary-action" :disabled="saving || uploading" @click="closeEditor">Abbrechen</v-btn>
          <v-btn outlined class="draft-action" :loading="saving && saveTarget === 'draft'" :disabled="saving || uploading" @click="saveProtocol('draft')">
            <v-icon left>mdi-content-save-edit-outline</v-icon>
            Als Entwurf speichern
          </v-btn>
          <v-btn
            color="#58d388"
            depressed
            class="complete-action"
            :loading="saving && saveTarget === 'completed'"
            :disabled="saving || uploading || !files.length"
            @click="saveProtocol('completed')">
            <v-icon left>mdi-check-circle-outline</v-icon>
            Abschliessen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="lightboxDialog" dark max-width="1100" content-class="protocol-lightbox-dialog">
      <v-card class="lightbox-card">
        <header>
          <div>
            <strong>{{ currentLightboxFile ? currentLightboxFile.originalName : "Foto" }}</strong>
            <small v-if="currentLightboxFile && currentLightboxFile.caption">{{ currentLightboxFile.caption }}</small>
          </div>
          <v-btn icon aria-label="Fotoansicht schliessen" @click="lightboxDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <div v-if="currentLightboxFile" class="lightbox-media">
          <img v-if="currentLightboxFile.previewUrl" :src="currentLightboxFile.previewUrl" :alt="currentLightboxFile.originalName" />
          <secured-media
            v-else
            :src="protocolPreviewPath(activeSession._id, currentLightboxFile.fileId, 'large')"
            :cache-key="`training-protocol-lightbox-${currentLightboxFile.fileId}`"
            media-type="image"
            eager></secured-media>
        </div>
        <footer v-if="previewableFiles.length > 1">
          <v-btn icon :disabled="lightboxIndex === 0" aria-label="Vorheriges Foto" @click="lightboxIndex -= 1"><v-icon>mdi-chevron-left</v-icon></v-btn>
          <span>{{ lightboxIndex + 1 }} / {{ previewableFiles.length }}</span>
          <v-btn icon :disabled="lightboxIndex === previewableFiles.length - 1" aria-label="Nächstes Foto" @click="lightboxIndex += 1"
            ><v-icon>mdi-chevron-right</v-icon></v-btn
          >
        </footer>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import SecuredMedia from "@/views/akademie/SecuredMedia.vue"
import { uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"

const MAX_FILES = 50
const MAX_FILE_SIZE = 50 * 1024 * 1024
const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "image/avif",
  "image/gif",
  "image/heic",
  "image/heif",
  "image/jpeg",
  "image/png",
  "image/webp",
])
const MIME_BY_EXTENSION = {
  avif: "image/avif",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heif",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  pdf: "application/pdf",
  png: "image/png",
  webp: "image/webp",
}

export default {
  name: "TrainingProtocolHub",
  components: { SecuredMedia },
  props: {
    sessions: { type: Array, default: () => [] },
  },
  data() {
    return {
      protocols: [],
      loadingProtocols: false,
      archiveDialog: false,
      archiveSearch: "",
      archiveYear: "all",
      archiveStatus: "all",
      editorDialog: false,
      editorLoading: false,
      activeSession: null,
      protocol: null,
      form: { title: "", summary: "" },
      files: [],
      uploading: false,
      uploadProgress: 0,
      uploadLabel: "",
      saving: false,
      saveTarget: null,
      dragIndex: null,
      lightboxDialog: false,
      lightboxIndex: 0,
    }
  },
  computed: {
    protocolMap() {
      const map = new Map()
      this.protocols.forEach((protocol) => {
        const sessionId = protocol && protocol.session && (protocol.session._id || protocol.session)
        if (sessionId) map.set(String(sessionId), protocol)
      })
      return map
    },
    archiveItems() {
      const now = Date.now()
      return (this.sessions || [])
        .filter((session) => {
          const startsAt = new Date(session.startsAt).getTime()
          return Number.isFinite(startsAt) && startsAt < now
        })
        .map((session) => {
          const protocol = this.protocolMap.get(String(session._id)) || null
          return { session, protocol, status: protocol ? protocol.status : "missing" }
        })
        .sort((left, right) => new Date(right.session.startsAt) - new Date(left.session.startsAt))
    },
    archiveCounts() {
      return this.archiveItems.reduce(
        (counts, item) => {
          counts[item.status] += 1
          return counts
        },
        { completed: 0, draft: 0, missing: 0 },
      )
    },
    archiveYearOptions() {
      const years = [...new Set(this.archiveItems.map((item) => new Date(item.session.startsAt).getFullYear()).filter(Number.isFinite))].sort(
        (left, right) => right - left,
      )
      return [{ text: "Alle Jahre", value: "all" }, ...years.map((year) => ({ text: String(year), value: String(year) }))]
    },
    filteredArchiveItems() {
      const search = String(this.archiveSearch || "")
        .trim()
        .toLowerCase()
      return this.archiveItems.filter((item) => {
        if (this.archiveStatus !== "all" && item.status !== this.archiveStatus) return false
        if (this.archiveYear !== "all" && String(new Date(item.session.startsAt).getFullYear()) !== String(this.archiveYear)) return false
        if (!search) return true
        return [item.session.title, item.session.location, item.protocol && item.protocol.title, item.protocol && item.protocol.summary]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search))
      })
    },
    activeSessionMeta() {
      if (!this.activeSession) return "Schulungsdetails werden geladen"
      const date = this.formatLongDate(this.activeSession.startsAt)
      const start = this.formatTime(this.activeSession.startsAt)
      const end = this.formatTime(this.activeSession.endsAt)
      return [date, start && end ? `${start} – ${end}` : "", this.activeSession.location || "Ort nicht hinterlegt"].filter(Boolean).join(" · ")
    },
    previewableFiles() {
      return this.files.filter((file) => this.canPreviewImage(file))
    },
    currentLightboxFile() {
      return this.previewableFiles[this.lightboxIndex] || null
    },
  },
  mounted() {
    this.loadProtocols()
  },
  beforeDestroy() {
    this.revokePreviewUrls()
  },
  methods: {
    notify(color, text) {
      this.$emit("notify", { color, text })
    },
    errorMessage(error, fallback) {
      return (error && error.response && error.response.data && error.response.data.error) || (error && error.message) || fallback
    },
    async loadProtocols() {
      this.loadingProtocols = true
      try {
        this.protocols = (await api.akademie.fetchTrainingProtocols()) || []
        this.$emit("loaded", this.protocols)
      } catch (error) {
        this.notify("error", this.errorMessage(error, "Fotoprotokolle konnten nicht geladen werden"))
      } finally {
        this.loadingProtocols = false
      }
    },
    openArchive() {
      this.archiveDialog = true
      this.loadProtocols()
    },
    async openEditor(session) {
      if (!session || !session._id) return
      if (this.editorDialog && !(await this.cancelPendingUploads())) return
      this.revokePreviewUrls()
      this.activeSession = session
      this.protocol = null
      this.form = { title: this.defaultTitle(session), summary: "" }
      this.files = []
      this.editorDialog = true
      this.editorLoading = true
      try {
        const protocol = await api.akademie.fetchTrainingProtocol(session._id)
        this.applyProtocol(protocol)
      } catch (error) {
        this.notify("error", this.errorMessage(error, "Fotoprotokoll konnte nicht geöffnet werden"))
        this.editorDialog = false
        this.activeSession = null
      } finally {
        this.editorLoading = false
      }
    },
    applyProtocol(protocol) {
      this.revokePreviewUrls()
      this.protocol = protocol || null
      this.form = {
        title: (protocol && protocol.title) || this.defaultTitle(this.activeSession),
        summary: (protocol && protocol.summary) || "",
      }
      this.files = ((protocol && protocol.files) || []).map((file, index) => ({
        ...file,
        key: file.fileId || file._id || `stored-${index}`,
        caption: file.caption || "",
        isCover: Boolean(file.isCover),
        previewUrl: null,
        session: null,
      }))
      this.ensureCover()
    },
    defaultTitle(session) {
      if (!session) return "Fotoprotokoll"
      return ["Fotoprotokoll", session.title, this.formatShortDate(session.startsAt)].filter(Boolean).join(" – ")
    },
    async closeEditor() {
      if (this.saving || this.uploading) return
      if (!(await this.cancelPendingUploads())) {
        this.notify("error", "Nicht gespeicherte Uploads konnten noch nicht verworfen werden")
        return
      }
      this.editorDialog = false
      this.lightboxDialog = false
      this.revokePreviewUrls()
      this.files = []
      this.protocol = null
      this.activeSession = null
    },
    openFilePicker() {
      if (this.saving || this.uploading || this.files.length >= MAX_FILES) return
      this.$refs.fileInput.click()
    },
    handleFileInput(event) {
      const files = event && event.target ? event.target.files : null
      this.addFiles(files)
      if (event && event.target) event.target.value = ""
    },
    handleFileDrop(event) {
      if (this.saving || this.uploading || this.files.length >= MAX_FILES) return
      this.addFiles(event.dataTransfer && event.dataTransfer.files)
    },
    fileMimeType(file) {
      const provided = String((file && file.type) || "")
        .trim()
        .toLowerCase()
      if (provided && provided !== "application/octet-stream") return provided
      return MIME_BY_EXTENSION[this.fileExtension(file && file.name).toLowerCase()] || provided
    },
    async addFiles(fileList) {
      const selected = Array.from(fileList || [])
      if (!selected.length || !this.activeSession) return
      if (this.files.length + selected.length > MAX_FILES) {
        this.notify("error", `Pro Fotoprotokoll sind maximal ${MAX_FILES} Dateien erlaubt`)
        return
      }
      const invalid = selected.find((file) => !ALLOWED_MIME_TYPES.has(this.fileMimeType(file)))
      if (invalid) {
        this.notify("error", "Erlaubt sind JPG, PNG, WebP, AVIF, GIF, HEIC, HEIF und PDF")
        return
      }
      const invalidSize = selected.find((file) => !Number.isFinite(file.size) || file.size < 1 || file.size > MAX_FILE_SIZE)
      if (invalidSize) {
        this.notify("error", invalidSize.size > MAX_FILE_SIZE ? `${invalidSize.name} ist grösser als 50 MB` : `${invalidSize.name} ist leer`)
        return
      }

      this.uploading = true
      this.uploadProgress = 0
      try {
        for (let index = 0; index < selected.length; index += 1) {
          const file = selected[index]
          let session = null
          this.uploadLabel = `${file.name} wird hochgeladen`
          try {
            session = await api.akademie.prepareTrainingProtocolMediaUpload(this.activeSession._id, file)
            await uploadCloudflareMedia(file, session, {
              onProgress: (loaded, total) => {
                const current = loaded / Math.max(1, total || file.size)
                this.uploadProgress = ((index + current) / selected.length) * 100
              },
            })
            const mimeType = this.fileMimeType(file)
            const previewUrl = mimeType.startsWith("image/") && !["image/heic", "image/heif"].includes(mimeType) ? URL.createObjectURL(file) : null
            this.files.push({
              key: session.objectKey,
              fileId: null,
              originalName: file.name,
              mimeType,
              size: file.size,
              caption: "",
              isCover: false,
              previewUrl,
              session,
            })
            this.ensureCover()
          } catch (error) {
            if (session) await api.akademie.cancelTrainingProtocolMediaUpload(this.activeSession._id, session).catch(() => null)
            throw error
          }
        }
        this.uploadProgress = 100
        this.notify("success", selected.length === 1 ? "Datei hochgeladen" : `${selected.length} Dateien hochgeladen`)
      } catch (error) {
        this.notify("error", this.errorMessage(error, "Upload fehlgeschlagen"))
      } finally {
        this.uploading = false
        this.uploadLabel = ""
        this.uploadProgress = 0
      }
    },
    async removeFile(index) {
      const file = this.files[index]
      if (!file) return
      if (file.session && this.activeSession) {
        try {
          await api.akademie.cancelTrainingProtocolMediaUpload(this.activeSession._id, file.session)
        } catch (error) {
          this.notify("error", "Die nicht gespeicherte Datei konnte nicht vollständig entfernt werden")
          return
        }
      }
      this.files.splice(index, 1)
      if (file.previewUrl) URL.revokeObjectURL(file.previewUrl)
      this.ensureCover()
    },
    async cancelPendingUploads() {
      if (!this.activeSession) return true
      const pending = this.files.filter((file) => file.session)
      if (!pending.length) return true
      const results = await Promise.all(
        pending.map((file) =>
          api.akademie
            .cancelTrainingProtocolMediaUpload(this.activeSession._id, file.session)
            .then(() => ({ file, success: true }))
            .catch(() => ({ file, success: false })),
        ),
      )
      const failedKeys = new Set(results.filter((result) => !result.success).map((result) => result.file.key))
      results.forEach((result) => {
        if (result.success && result.file.previewUrl) URL.revokeObjectURL(result.file.previewUrl)
      })
      this.files = this.files.filter((file) => !file.session || failedKeys.has(file.key))
      this.ensureCover()
      return failedKeys.size === 0
    },
    revokePreviewUrls() {
      this.files.forEach((file) => {
        if (file.previewUrl) URL.revokeObjectURL(file.previewUrl)
      })
    },
    moveFile(index, direction) {
      const target = index + direction
      if (target < 0 || target >= this.files.length) return
      const next = [...this.files]
      const [file] = next.splice(index, 1)
      next.splice(target, 0, file)
      this.files = next
    },
    startFileDrag(index) {
      this.dragIndex = index
    },
    dropFileAt(index) {
      if (this.dragIndex === null || this.dragIndex === index) {
        this.dragIndex = null
        return
      }
      const next = [...this.files]
      const [file] = next.splice(this.dragIndex, 1)
      next.splice(index, 0, file)
      this.files = next
      this.dragIndex = null
    },
    setCover(index) {
      if (!this.files[index] || !this.isImage(this.files[index])) return
      this.files = this.files.map((file, fileIndex) => ({ ...file, isCover: fileIndex === index }))
    },
    ensureCover() {
      const current = this.files.findIndex((file) => file.isCover && this.isImage(file))
      if (current >= 0) {
        this.files.forEach((file, index) => {
          file.isCover = index === current
        })
        return
      }
      const firstImage = this.files.findIndex((file) => this.isImage(file))
      this.files.forEach((file, index) => {
        file.isCover = index === firstImage
      })
    },
    async saveProtocol(status) {
      if (!this.activeSession || this.saving || this.uploading) return
      if (!String(this.form.title || "").trim()) {
        this.notify("error", "Bitte gib dem Fotoprotokoll einen Titel")
        return
      }
      if (status === "completed" && !this.files.length) {
        this.notify("error", "Zum Abschliessen wird mindestens eine Datei benötigt")
        return
      }
      this.saving = true
      this.saveTarget = status
      try {
        const payload = {
          title: this.form.title,
          summary: this.form.summary,
          status,
          files: this.files.map((file) => ({
            ...(file.fileId ? { fileId: file.fileId } : { upload: file.session }),
            caption: file.caption || "",
            isCover: Boolean(file.isCover),
          })),
        }
        const saved = await api.akademie.saveTrainingProtocol(this.activeSession._id, payload)
        this.protocol = saved
        const index = this.protocols.findIndex((protocol) => protocol._id === saved._id)
        if (index >= 0) this.$set(this.protocols, index, saved)
        else this.protocols.unshift(saved)
        this.$emit("loaded", this.protocols)
        this.$emit("saved", saved)
        this.revokePreviewUrls()
        this.files = []
        this.editorDialog = false
        this.lightboxDialog = false
        this.notify("success", status === "completed" ? "Fotoprotokoll abgeschlossen" : "Entwurf gespeichert")
        this.activeSession = null
        this.protocol = null
      } catch (error) {
        this.notify("error", this.errorMessage(error, "Fotoprotokoll konnte nicht gespeichert werden"))
      } finally {
        this.saving = false
        this.saveTarget = null
      }
    },
    coverFile(protocol) {
      if (!protocol || !Array.isArray(protocol.files)) return null
      return protocol.files.find((file) => file.isCover && this.isImage(file)) || protocol.files.find((file) => this.isImage(file)) || null
    },
    isImage(file) {
      return String((file && file.mimeType) || "").startsWith("image/")
    },
    isPdf(file) {
      return String((file && file.mimeType) || "") === "application/pdf"
    },
    canPreviewImage(file) {
      return this.isImage(file) && Boolean(file.previewUrl || file.fileId)
    },
    openFile(file) {
      if (this.canPreviewImage(file)) {
        const index = this.previewableFiles.findIndex((item) => item.key === file.key)
        this.lightboxIndex = Math.max(0, index)
        this.lightboxDialog = true
        return
      }
      if (file.fileId && this.activeSession) this.downloadFile(file)
    },
    async downloadFile(file) {
      try {
        await api.akademie.downloadTrainingProtocolFile(this.activeSession._id, file.fileId, file.originalName)
      } catch (error) {
        this.notify("error", "Datei konnte nicht heruntergeladen werden")
      }
    },
    protocolPreviewPath(sessionId, fileId, variant) {
      return api.akademie.getTrainingProtocolPreviewPath(sessionId, fileId, variant)
    },
    statusLabel(status) {
      if (status === "completed") return "Fertig"
      if (status === "draft") return "Entwurf"
      return "Noch offen"
    },
    updatedText(protocol) {
      if (!protocol) return ""
      const user = protocol.updatedBy
      const name = user && [user.firstName, user.lastName].filter(Boolean).join(" ")
      return [name ? `Bearbeitet von ${name}` : "Zuletzt bearbeitet", this.formatDateTime(protocol.updatedAt)].filter(Boolean).join(" · ")
    },
    formatLongDate(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return "Datum nicht hinterlegt"
      return date.toLocaleDateString("de-CH", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
    },
    formatShortDate(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ""
      return date.toLocaleDateString("de-CH")
    },
    formatTime(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ""
      return date.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    formatDateTime(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ""
      return date.toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    },
    formatBytes(value) {
      const bytes = Number(value)
      if (!Number.isFinite(bytes) || bytes < 1) return "Dateigrösse unbekannt"
      if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
      return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`
    },
    fileExtension(value) {
      const parts = String(value || "Datei").split(".")
      return parts.length > 1 ? parts.pop().toUpperCase() : "DATEI"
    },
  },
}
</script>

<style scoped>
.training-protocol-hub {
  font-size: 1rem;
}

.hidden-file-input {
  display: none;
}

.archive-card,
.editor-card {
  --surface: rgba(255, 255, 255, 0.04);
  --surface-strong: rgba(255, 255, 255, 0.065);
  --border: rgba(255, 255, 255, 0.1);
  --muted: #a3a3a8;
  display: flex;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.25rem !important;
  background: #1b1c1c !important;
  font-size: 1rem;
}

.dialog-header {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: 3rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.1), transparent 55%), rgba(24, 25, 25, 0.98);
}

.editor-header {
  grid-template-columns: 3rem minmax(0, 1fr) auto auto;
}

.dialog-header-icon {
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border: 1px solid rgba(88, 211, 136, 0.24);
  border-radius: 0.9375rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.13);
}

.dialog-header-icon .v-icon {
  color: currentColor;
}

.dialog-header-copy {
  min-width: 0;
}

.dialog-header-copy > div {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.dialog-header-copy h2 {
  margin: 0.125rem 0 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 1.375rem;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dialog-header-copy p {
  margin: 0.25rem 0 0;
  overflow: hidden;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.close-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.04) !important;
}

.archive-body,
.editor-body {
  min-height: 0;
  flex: 1 1 auto;
  padding: 1.25rem !important;
  overflow-y: auto;
  background: radial-gradient(circle at 12% 0, rgba(0, 150, 64, 0.065), transparent 25rem), #151616;
}

.archive-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.archive-stats article {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: var(--surface);
}

.stat-icon {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
}

.stat-icon.completed {
  color: #72e09d;
  background: rgba(0, 150, 64, 0.16);
}

.stat-icon.draft {
  color: #f2cd68;
  background: rgba(242, 205, 104, 0.12);
}

.stat-icon.missing {
  color: #a3a3a8;
  background: rgba(255, 255, 255, 0.07);
}

.stat-icon .v-icon {
  color: currentColor;
}

.archive-stats article > div {
  display: grid;
}

.archive-stats strong {
  color: #ffffff;
  font-size: 1.375rem;
  line-height: 1.1;
}

.archive-stats small {
  margin-top: 0.125rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.archive-filter-panel {
  display: grid;
  grid-template-columns: minmax(16rem, 1fr) 10rem auto;
  align-items: center;
  gap: 0.625rem;
  margin-top: 0.75rem;
  padding: 0.625rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.archive-filter-panel ::v-deep .v-label,
.archive-filter-panel ::v-deep input,
.archive-filter-panel ::v-deep .v-select__selection {
  font-size: 0.875rem !important;
}

.archive-status-filter {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem !important;
  background: rgba(0, 0, 0, 0.14) !important;
}

.archive-status-filter ::v-deep .v-btn {
  min-width: 0 !important;
  min-height: 2.5rem !important;
  padding: 0 0.625rem !important;
  color: #a3a3a8 !important;
  font-size: 0.75rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.archive-status-filter ::v-deep .v-btn--active {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.12) !important;
}

.archive-loading,
.editor-loading {
  display: flex;
  min-height: 18rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.75rem;
  color: var(--muted);
  font-size: 0.875rem;
}

.archive-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.archive-item {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.0625rem;
  background: var(--surface);
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease;
}

.archive-item:hover {
  border-color: rgba(88, 211, 136, 0.32);
  background: var(--surface-strong);
  transform: translateY(-0.125rem);
}

.archive-item:focus-visible,
.file-preview:focus-visible,
.upload-dropzone:focus-visible {
  outline: 0.125rem solid #58d388;
  outline-offset: 0.125rem;
}

.archive-cover {
  position: relative;
  height: 9.5rem;
  overflow: hidden;
  border-bottom: 1px solid var(--border);
  background: radial-gradient(circle at 25% 20%, rgba(0, 150, 64, 0.18), transparent 65%), #202222;
}

.archive-cover-media,
.archive-cover-media ::v-deep .secured-media-content,
.archive-cover-media ::v-deep .secured-media-wrapper {
  width: 100%;
  height: 100%;
}

.archive-cover-media ::v-deep .secured-media-content {
  object-fit: cover;
}

.archive-cover-placeholder {
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
}

.archive-cover-placeholder .v-icon {
  color: rgba(255, 255, 255, 0.22);
  font-size: 3rem;
}

.archive-status-pill,
.editor-status-pill {
  padding: 0.3125rem 0.625rem;
  border: 1px solid currentColor;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
}

.archive-status-pill {
  position: absolute;
  top: 0.625rem;
  left: 0.625rem;
  background: rgba(17, 18, 18, 0.88);
  backdrop-filter: blur(0.5rem);
}

.is-completed {
  color: #72e09d;
}

.is-draft {
  color: #f2cd68;
}

.is-missing {
  color: #c2c2c6;
}

.archive-file-count {
  position: absolute;
  right: 0.625rem;
  bottom: 0.625rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.3125rem 0.5rem;
  border-radius: 999px;
  color: #ffffff;
  background: rgba(17, 18, 18, 0.82);
  font-size: 0.75rem;
  font-weight: 750;
}

.archive-file-count .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.archive-item-copy {
  padding: 0.875rem;
}

.archive-item-copy > small {
  display: block;
  overflow: hidden;
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 750;
  text-overflow: ellipsis;
  text-transform: capitalize;
  white-space: nowrap;
}

.archive-item-copy h3 {
  margin: 0.25rem 0 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 1.0625rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.archive-item-copy p {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0.375rem 0 0;
  overflow: hidden;
  color: var(--muted);
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.archive-item-copy p .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.archive-item-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.625rem;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  color: #c9c9cc;
}

.archive-item-footer span {
  overflow: hidden;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.archive-item-footer .v-icon {
  flex: 0 0 auto;
  color: #58d388;
}

.archive-empty,
.files-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 1px dashed rgba(255, 255, 255, 0.13);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.02);
  text-align: center;
}

.archive-empty {
  min-height: 17rem;
  margin-top: 0.75rem;
}

.archive-empty > span,
.files-empty > span {
  display: grid;
  width: 3.25rem;
  height: 3.25rem;
  place-items: center;
  border-radius: 0.9375rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.archive-empty .v-icon,
.files-empty .v-icon {
  color: currentColor;
}

.archive-empty h3,
.files-empty h4 {
  margin: 0.75rem 0 0;
  color: #ffffff;
  font-size: 1rem;
}

.archive-empty p,
.files-empty p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
}

.session-summary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1px solid rgba(88, 211, 136, 0.19);
  border-radius: 1rem;
  background: rgba(0, 150, 64, 0.08);
}

.session-summary > span {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.14);
}

.session-summary > span .v-icon {
  color: currentColor;
}

.session-summary > div {
  display: grid;
  min-width: 0;
}

.session-summary small {
  color: #75d99b;
  font-size: 0.75rem;
  font-weight: 750;
}

.session-summary strong {
  margin-top: 0.125rem;
  color: #ffffff;
  font-size: 1rem;
}

.session-summary p {
  margin: 0.125rem 0 0;
  color: #bcbcc0;
  font-size: 0.875rem;
}

.editor-section {
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: var(--surface);
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.section-heading > span:first-child {
  display: grid;
  width: 2.625rem;
  height: 2.625rem;
  flex: 0 0 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.section-heading > span:first-child .v-icon {
  color: currentColor;
}

.section-heading > div {
  min-width: 0;
  flex: 1 1 auto;
}

.section-heading h3 {
  margin: 0;
  color: #ffffff;
  font-size: 1rem;
}

.section-heading p {
  margin: 0.1875rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.45;
}

.details-section ::v-deep .v-input:last-child {
  margin-bottom: 0;
}

.details-section ::v-deep .v-label,
.protocol-file-card ::v-deep .v-label,
.protocol-file-card ::v-deep input {
  font-size: 0.875rem !important;
}

.details-section ::v-deep input,
.details-section ::v-deep textarea {
  font-size: 1rem !important;
}

.details-section ::v-deep .v-messages__message,
.details-section ::v-deep .v-counter {
  font-size: 0.75rem !important;
}

.media-count {
  padding: 0.3125rem 0.625rem;
  border-radius: 999px;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.07);
  font-size: 0.75rem;
  font-weight: 750;
}

.upload-dropzone {
  display: grid;
  width: 100%;
  min-height: 5rem;
  grid-template-columns: 2.75rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  border: 1px dashed rgba(88, 211, 136, 0.38);
  border-radius: 1rem;
  color: inherit;
  background: rgba(0, 150, 64, 0.065);
  cursor: pointer;
  text-align: left;
  transition: border-color 160ms ease, background 160ms ease;
}

.upload-dropzone:hover:not(:disabled) {
  border-color: rgba(88, 211, 136, 0.72);
  background: rgba(0, 150, 64, 0.11);
}

.upload-dropzone:disabled {
  cursor: default;
  opacity: 0.55;
}

.upload-dropzone-icon {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.15);
}

.upload-dropzone-icon .v-icon,
.upload-dropzone-arrow {
  color: currentColor;
}

.upload-dropzone-copy {
  display: grid;
}

.upload-dropzone-copy strong {
  color: #ffffff;
  font-size: 0.9375rem;
}

.upload-dropzone-copy small {
  margin-top: 0.125rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.upload-dropzone-arrow {
  color: #58d388;
}

.upload-progress-panel {
  margin-top: 0.625rem;
  padding: 0.75rem;
  border: 1px solid rgba(88, 211, 136, 0.18);
  border-radius: 0.875rem;
  background: rgba(0, 150, 64, 0.06);
}

.upload-progress-panel > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
  color: #d4d4d8;
  font-size: 0.75rem;
}

.protocol-files-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.protocol-file-card {
  min-width: 0;
  padding: 0.625rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(0, 0, 0, 0.15);
  transition: opacity 150ms ease, border-color 150ms ease;
}

.protocol-file-card.is-cover {
  border-color: rgba(88, 211, 136, 0.38);
}

.protocol-file-card.is-dragging {
  opacity: 0.5;
}

.file-preview {
  position: relative;
  height: 10rem;
  overflow: hidden;
  border-radius: 0.75rem;
  background: #232424;
  cursor: pointer;
}

.file-preview > img,
.file-preview ::v-deep .secured-media-wrapper,
.file-preview ::v-deep .secured-media-content {
  width: 100%;
  height: 100%;
}

.file-preview > img,
.file-preview ::v-deep .secured-media-content {
  object-fit: cover;
}

.file-placeholder {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.375rem;
  color: #9ca0a2;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.06), transparent 70%);
}

.file-placeholder.pdf {
  color: #ef7f7f;
}

.file-placeholder .v-icon {
  color: currentColor;
  font-size: 3rem;
}

.file-placeholder small {
  font-size: 0.75rem;
  font-weight: 800;
}

.cover-label,
.new-file-label {
  position: absolute;
  top: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 800;
  backdrop-filter: blur(0.5rem);
}

.cover-label {
  left: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.1875rem;
  color: #8beaaf;
  background: rgba(0, 80, 34, 0.82);
}

.cover-label .v-icon {
  color: currentColor;
  font-size: 0.875rem;
}

.new-file-label {
  right: 0.5rem;
  color: #ffffff;
  background: rgba(23, 24, 24, 0.82);
}

.file-copy {
  display: grid;
  margin: 0.625rem 0;
}

.file-copy strong {
  overflow: hidden;
  color: #ffffff;
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-copy small {
  margin-top: 0.125rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.file-actions {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  margin-top: 0.5rem;
}

.file-actions .v-btn {
  width: 2.25rem !important;
  min-width: 2.25rem !important;
  height: 2.25rem !important;
  color: #a9a9ad !important;
}

.file-actions .v-btn.active {
  color: #58d388 !important;
  background: rgba(0, 150, 64, 0.12) !important;
}

.remove-file-action {
  color: #ef8b8b !important;
}

.files-empty {
  min-height: 12rem;
  margin-top: 0.75rem;
  padding: 1rem;
}

.protocol-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  color: var(--muted);
  background: rgba(255, 255, 255, 0.025);
  font-size: 0.75rem;
}

.protocol-meta .v-icon {
  color: #858589;
  font-size: 1rem;
}

.protocol-meta span + span::before {
  margin-right: 0.5rem;
  content: "·";
}

.editor-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1rem !important;
  border-top: 1px solid var(--border);
  background: #1a1b1b;
}

.editor-actions-copy {
  display: grid;
  min-width: 0;
  flex: 1 1 auto;
}

.editor-actions-copy strong {
  color: #ffffff;
  font-size: 0.875rem;
}

.editor-actions-copy small {
  margin-top: 0.125rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.secondary-action,
.draft-action,
.complete-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.secondary-action {
  color: #b8b8bc !important;
}

.draft-action {
  border-color: rgba(255, 255, 255, 0.19) !important;
  color: #ffffff !important;
}

.complete-action {
  color: #07120b !important;
}

.lightbox-card {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.125rem !important;
  background: #101111 !important;
}

.lightbox-card > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.lightbox-card > header > div {
  display: grid;
  min-width: 0;
}

.lightbox-card > header strong {
  overflow: hidden;
  color: #ffffff;
  font-size: 0.9375rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lightbox-card > header small {
  margin-top: 0.125rem;
  color: #a3a3a8;
  font-size: 0.75rem;
}

.lightbox-media {
  height: min(72vh, 48rem);
  height: min(72dvh, 48rem);
  background: #090a0a;
}

.lightbox-media > img,
.lightbox-media ::v-deep .secured-media-wrapper,
.lightbox-media ::v-deep .secured-media-content {
  width: 100%;
  height: 100%;
}

.lightbox-media > img,
.lightbox-media ::v-deep .secured-media-content {
  object-fit: contain;
}

.lightbox-card > footer {
  display: flex;
  min-height: 3.5rem;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
  color: #d4d4d8;
  font-size: 0.875rem;
}

@media (max-width: 70rem) {
  .archive-grid,
  .protocol-files-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .archive-filter-panel {
    grid-template-columns: minmax(14rem, 1fr) 9rem;
  }

  .archive-status-filter {
    grid-column: 1 / -1;
    justify-self: stretch;
  }

  .archive-status-filter ::v-deep .v-btn {
    flex: 1 1 0;
  }

  .editor-actions-copy {
    display: none;
  }
}

@media (max-width: 60rem) {
  .editor-actions {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) minmax(0, 1fr);
  }

  .draft-action,
  .complete-action {
    min-width: 0 !important;
  }
}

@media (max-width: 37.5rem) {
  .archive-card,
  .editor-card {
    width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0 !important;
  }

  .dialog-header {
    grid-template-columns: 2.75rem minmax(0, 1fr) 2.75rem;
    gap: 0.625rem;
    padding: 0.75rem;
  }

  .editor-header {
    grid-template-columns: 2.75rem minmax(0, 1fr) 2.75rem;
  }

  .dialog-header-icon {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.8125rem;
  }

  .dialog-header-copy h2 {
    font-size: 1.0625rem;
  }

  .dialog-header-copy p,
  .editor-status-pill {
    display: none;
  }

  .archive-body,
  .editor-body {
    padding: 0.75rem !important;
  }

  .archive-stats {
    gap: 0.375rem;
  }

  .archive-stats article {
    display: grid;
    justify-items: center;
    gap: 0.375rem;
    padding: 0.625rem 0.25rem;
    text-align: center;
  }

  .stat-icon {
    width: 2.5rem;
    height: 2.5rem;
    flex-basis: 2.5rem;
  }

  .archive-stats strong {
    font-size: 1.125rem;
  }

  .archive-filter-panel {
    grid-template-columns: minmax(0, 1fr);
  }

  .archive-status-filter {
    grid-column: 1;
    width: 100%;
  }

  .archive-status-filter ::v-deep .v-btn {
    padding: 0 0.25rem !important;
  }

  .archive-grid,
  .protocol-files-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .archive-cover {
    height: 10.5rem;
  }

  .session-summary {
    align-items: flex-start;
    padding: 0.75rem;
  }

  .session-summary p {
    line-height: 1.45;
  }

  .editor-section {
    padding: 0.75rem;
  }

  .section-heading {
    align-items: flex-start;
    gap: 0.625rem;
  }

  .media-heading {
    display: grid;
    grid-template-columns: 2.625rem minmax(0, 1fr);
  }

  .media-count {
    grid-column: 2;
    justify-self: start;
  }

  .upload-dropzone {
    grid-template-columns: 2.75rem minmax(0, 1fr);
  }

  .upload-dropzone-arrow {
    display: none;
  }

  .file-preview {
    height: 13rem;
  }

  .protocol-meta {
    align-items: flex-start;
    flex-direction: column;
  }

  .protocol-meta span + span::before {
    display: none;
  }

  .editor-actions {
    grid-template-columns: minmax(0, 0.65fr) minmax(0, 1fr);
    padding: 0.75rem !important;
  }

  .secondary-action {
    display: none;
  }

  .draft-action,
  .complete-action {
    width: 100%;
    padding: 0 0.5rem !important;
  }

  .lightbox-card {
    border-radius: 0 !important;
  }
}
</style>
